import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Observable } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { differenceInCalendarDays, format } from 'date-fns';
import { saveAs } from 'file-saver';
import { NgxSpinnerService } from 'ngx-spinner';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';

import { AppOptionsService, AuthService, DynamicFormService, ErrorsHandlingService } from '../../../core/services';
import { CompanyInterface, PartnerInterfaceGlobal } from '../../../core/models';
import { DeleteEndpointConfirmComponent } from '../../../shared/components';
import { companySettings, newCompany } from '../../../core/config';
import { ErrorValidator } from '../../../core/validators';
import { UserRole } from '../../../core/enum';

import { CompaniesNewComponent } from '../modals';

@Injectable({
  providedIn: 'root'
})
export class CompaniesService {
  public companies: CompanyInterface.ICompany[] = [];
  public userRole = UserRole.viewer;
  public companiesState: { [key: number]: boolean } = {};

  public companyNewForm: FormGroup;
  public settingsCompanyForm: FormGroup;
  public searchForm: FormGroup;
  public startDate: Date;

  public disabledDateForCompanySettings = (current: Date): boolean =>
    // Can not select days before today and today
    differenceInCalendarDays(current, this.startDate) < 0;

  public disabledDateForNewCompany = (current: Date): boolean =>
    // Can not select days before today and today
    differenceInCalendarDays(current, new Date()) < 0;

  private timer: number;
  private apiUrl = '';

  private partner: PartnerInterfaceGlobal.IPartnerSettings;

  constructor(
    private http: HttpClient,
    private options: AppOptionsService,
    private errorsHandlingService: ErrorsHandlingService,
    private authService: AuthService,
    private loadingService: NgxSpinnerService,
    private modalService: NzModalService,
    private dynamicFormService: DynamicFormService,
    private fb: FormBuilder,
  ) {
    this.apiUrl = this.options.getApiUrl();
    this.userRole = this.authService.userRole;
  }

  public initComponent(): void {
    this.companies = [];
    this.createForm();
    this.getCompaniesHandler();
    this.partner = this.authService.partner as any;
  }

  public get formValue() {
    return this.settingsCompanyForm.getRawValue();
  }

  public get start() {
    return this.settingsCompanyForm.get('start')?.value;
  }

  public get isUserViewer(): boolean {
    return this.userRole === UserRole.viewer;
  }

  public get isAvailableCompanyEdit(): boolean {
    return ['manager', 'support'].indexOf(this.userRole) !== -1;
  }

  public get isShowCompanyCreateBtn(): boolean {
    return [UserRole.manager, UserRole.support, UserRole.viewer].indexOf(this.userRole) !== -1;
  }

  public onValidateCredentialsHandler(event: KeyboardEvent) {
    const inp = String.fromCharCode(event.keyCode);

    if (/^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/.test(inp)) {
      return true;
    } else {
      event.preventDefault();
      this.errorsHandlingService.showNotification('error', 'error', 'error', 'This field is empty or incorrect', null);
      return false;
    }
  }

  public isCompanyOpenedHandler(id: number): boolean {
    return this.companiesState[id];
  }

  public toggleCompanyStateHandler(id: number): void {
    Object.keys(this.companiesState).forEach((key: string) => {
      const numericKey = Number(key);
      if (numericKey !== id) {
        this.companiesState[numericKey] = false;
      }
    });
    this.companiesState[id] = !this.companiesState[id];
  }


  public onUpdateCompanyStateHandler(id: number): void {
    const company = this.companies.find(company => company.id === id);

    if (company) {
      this.toggleCompanyStateHandler(id);
    }
  }

  public onCancelHandler(i: number): void {
    this.toggleCompanyStateHandler(i);
  }

  public formatDateHandler(date: string): string {
    if (!date) {
      return date;
    }
    return date.split(' ')[0];
  }

  public onChangeSwitchHandler(event: boolean): void {
    this.changeDisableStateForFormControl(event);
  }

  public clearNonDigitHandler(e: Event): void {
    (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.replace(/\D/g, '');
  }

  public onTermChangeHandler(_: Event): void {
    let date = new Date(this.start);
    date.setDate(date.getDate() + this.terms);
    this.settingsCompanyForm.get('invoice')?.patchValue(date.toISOString().slice(0, 10));
  }

  public onChangeStorageHandler(_: Event) {
    const { cookie_storage } = this.formValue;

    if (cookie_storage === 'PLATFORM') {
      this.settingsCompanyForm.controls['sync_url'].setErrors(null);
      this.settingsCompanyForm.controls['sync_url_iframe'].setErrors(null);
      this.settingsCompanyForm.controls['sync_url_iframe'].markAsUntouched();
      this.settingsCompanyForm.controls['sync_url_iframe'].markAsUntouched();
    }
  }

  public onRubiconActiveHandler(_: Event): void {
    const { rubicon_active } = this.formValue;
    if (rubicon_active) {
      this.settingsCompanyForm.get('rubicon_username')?.addValidators([Validators.required]);
      this.settingsCompanyForm.get('rubicon_password')?.addValidators([Validators.required]);
    } else {
      this.settingsCompanyForm.get('rubicon_username')?.removeValidators([Validators.required]);
      this.settingsCompanyForm.get('rubicon_password')?.removeValidators([Validators.required]);
    }
    this.settingsCompanyForm.get('rubicon_username')?.updateValueAndValidity();
    this.settingsCompanyForm.get('rubicon_password')?.updateValueAndValidity();
  }

  public createCompanySettingsForm(company: CompanyInterface.ICompany): void {
    this.settingsCompanyForm = this.dynamicFormService.createDynamicFormGroup(companySettings);
    this.settingsCompanyForm = this.dynamicFormService.updateFormWithValues(this.settingsCompanyForm, company);

    if (this.partner.rubicon_settings && !this.partner.rubicon_settings.username && !this.partner.rubicon_settings.password) {
      this.settingsCompanyForm.get('rubicon_active')?.disable({ emitEvent: false });
    }

    if (company.rubicon_settings) {
      company.rubicon_settings.active
        ? this.settingsCompanyForm.get('rubicon_active')?.patchValue(true)
        : this.settingsCompanyForm.get('rubicon_active')?.patchValue(false);
      company.rubicon_settings.username
        ? this.settingsCompanyForm.get('rubicon_username')?.patchValue(company.rubicon_settings.username)
        : this.settingsCompanyForm.get('rubicon_username')?.patchValue('');
      company.rubicon_settings.password
        ? this.settingsCompanyForm.get('rubicon_password')?.patchValue(company.rubicon_settings.password)
        : this.settingsCompanyForm.get('rubicon_password')?.patchValue('');
    }

    this.settingsCompanyForm.controls.domain.addValidators([ErrorValidator.companyDomainValid]);
    const cookieSync = this.settingsCompanyForm.get('cookie_sync')?.value;
    this.settingsCompanyForm.get('cookie_sync')?.patchValue(cookieSync === 1);
    this.changeDisableStateForFormControl(cookieSync);
    this.settingsCompanyForm.get('start')?.patchValue(new Date(company.start as string));
  }

  public createNewCompanyForm(): void {
    this.companyNewForm = this.dynamicFormService.createDynamicFormGroup(newCompany);
    this.companyNewForm.controls.domain.addValidators([ErrorValidator.companyDomainValid]);
  }

  public onCreateNewCompanyHandler(dialog: NzModalRef<CompaniesNewComponent, any>): void {
    if (!this.onHandleNewCompanyFormErrors()) {
      return;
    }
    const { start, ...rest } = this.companyNewForm.value;
    const company = {
      start: (format(start, 'yyyy-MM-dd')),
      ...rest
    };

    this.createNewCompany(company)
      .subscribe(res => {
        this.errorsHandlingService.showNotification('success', 'success', 'success', `Company ${res.data.name} created`, null);
        dialog.close(res);
      },
        error => this.errorsHandlingService.handleError(error));
  }


  public onUpdateCompanyHandler(callback: () => void): void {
    if (!this.onHandleCompanySettingsFormErrors()) {
      return;
    }

    const { start, ...rest } = this.settingsCompanyForm.value;
    const company = {
      start: (format(start, 'yyyy-MM-dd')),
      ...rest
    };

    this.loadingService.show();
    this.updateCompany(company)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe((resp) => {
        callback();
        this.errorsHandlingService.showNotification('success', 'success', 'success', `Company ${resp.data.name} updated`, null);
        this.getCompaniesHandler();
      },
        error => this.errorsHandlingService.handleError(error));
  }

  public createNewCompanyHandler(): void {
    const dialogRef = this.modalService.create({
      nzContent: CompaniesNewComponent,
      nzClosable: false,
      nzMaskClosable: false,
      nzFooter: null,
      nzKeyboard: false,
      nzClassName: 'companies-new-modal',
      nzWrapClassName: 'companies-new-modal-wrap',
      nzStyle: { top: '100px', width: '650px' },
      nzBodyStyle: { width: '650px', padding: '0px', height: '335px' },
    });
    dialogRef.afterClose.subscribe(res => {
      switch (res.status) {
        case 'success':
          this.getCompaniesHandler();
          break;
        case 'cancel':
          break;
      }
    });
  }

  public onSearchHandler(_: Event, to = true): void {
    const { search } = this.searchForm.value;

    if (to) {
      clearTimeout(this.timer);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.timer = setTimeout(() => {
        this.getCompaniesHandler(search);
      }, 600);
    } else {
      this.getCompaniesHandler(search);
    }
  }

  public onDeleteCompanyHandler(id: number, name: string): void {
    const dialogRef = this.modalService.create({
      nzContent: DeleteEndpointConfirmComponent,
      nzMaskClosable: false,
      nzStyle: { top: '200px', width: '450px', height: 'auto' },
      nzBodyStyle: { width: '450px', height: 'auto' },
      nzData: {
        entity: 'company', name: name, type: 'company'
      }
    });

    dialogRef.afterClose.subscribe(res => {
      res && this.deleteCompanyHandler(id);
    });
  }

  public deleteCompanyHandler(companyId: number) {
    this.loadingService.show();
    this.deleteCompanyById(companyId)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(
        (resp) => {
          this.companies = resp.data.reverse();
          this.errorsHandlingService.showNotification('success', 'success', 'success', 'Company deleted!', null);
        },
        error => this.errorsHandlingService.handleError(error));
  }

  public getCompaniesHandler(searchValue = ''): void {
    this.loadingService.show();
    this.getCompanyList(searchValue)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(resp => {
        this.companies = resp.data;
      },
        (error) => this.errorsHandlingService.handleError(error));
  }

  public downloadAdsTxtHandler(companyId: number): void {
    this.downloadAdsTxt({ companyId: companyId })
      .subscribe(response => {
        const blob = new Blob([response], { type: 'text/plain;charset=utf-8' });
        saveAs(blob, 'Ads.txt');
      },
        () => this.errorsHandlingService.showNotification('error', 'error', 'error', 'Error with generating Ads.txt', null));
  }

  private createForm(): void {
    this.searchForm = this.fb.group({
      search: ['']
    });
  }

  private changeDisableStateForFormControl(state: boolean): void {
    if (state) {
      this.settingsCompanyForm.get('sync_url_iframe')?.enable();
      this.settingsCompanyForm.get('cookie_storage')?.enable();
    } else {
      this.settingsCompanyForm.get('sync_url_iframe')?.disable();
      this.settingsCompanyForm.get('cookie_storage')?.disable();
      this.settingsCompanyForm.controls['sync_url'].setErrors(null);
      this.settingsCompanyForm.controls['sync_url_iframe'].setErrors(null);
      this.settingsCompanyForm.controls['sync_url_iframe'].markAsUntouched();
      this.settingsCompanyForm.controls['sync_url_iframe'].markAsUntouched();
    }
  }

  private get terms() {
    return this.settingsCompanyForm.get('terms')?.value;
  }

  private onHandleNewCompanyFormErrors(): boolean {
    let errorMessages: string[] = [];
    const { name, email, domain } = this.companyNewForm.controls;

    errorMessages = this.validateDomain(errorMessages, domain);
    errorMessages = this.validateName(errorMessages, name);
    errorMessages = this.validateEmail(errorMessages, email);
    if (errorMessages.length) {
      errorMessages.forEach((message: string) => this.errorsHandlingService.showNotification('error', 'error', 'error', message, null));
      return false;
    }
    return true;
  }

  private onHandleCompanySettingsFormErrors(): boolean {
    let errorMessages: string[] = [];

    const { name, email, domain, rubicon_username, rubicon_password, rubicon_active } = this.settingsCompanyForm.controls;

    errorMessages = this.validateName(errorMessages, name);
    errorMessages = this.validateEmail(errorMessages, email);
    errorMessages = this.validateDomain(errorMessages, domain);

    if (rubicon_active.value) {
      errorMessages = this.validateRubiconUsername(errorMessages, rubicon_username);
      errorMessages = this.validateRubiconPassword(errorMessages, rubicon_password);
    }


    errorMessages = errorMessages.filter(message => !Number.isNaN(message));

    if (errorMessages.length) {
      errorMessages.forEach((message: string) => this.errorsHandlingService.showNotification('error', 'error', 'error', message, null));
      return false;
    }

    this.settingsCompanyForm.updateValueAndValidity();
    return true;
  }

  private validateName(errorMessages: string[], control: AbstractControl): string[] {
    const isRequiredCompanyName = control.errors?.required;

    if (isRequiredCompanyName) {
      errorMessages.push('Name is required');
    }

    return errorMessages;
  }

  private validateEmail(errorMessages: string[], control: AbstractControl): string[] {
    const isRequiredEmail = control.errors?.required;
    const emailIsInvalid = control.errors?.email;

    if (isRequiredEmail) {
      errorMessages.push('Email is required');
    }

    if (emailIsInvalid) {
      errorMessages.push('Email must be valid');
    }

    return errorMessages;
  }

  private validateDomain(errorMessages: string[], control: AbstractControl): string[] {
    const domainIsInvalid = control.errors?.invalidDomain;

    if (domainIsInvalid) {
      errorMessages.push('Domain must be valid');
      control.markAsTouched();
    }

    return errorMessages;
  }

  private validateRubiconUsername(errorMessages: string[], control: AbstractControl): string[] {
    const isRequiredUsername = control.errors?.required;

    if (isRequiredUsername) {
      errorMessages.push('Username is required');
    }

    return errorMessages;
  }

  private validateRubiconPassword(errorMessages: string[], control: AbstractControl): string[] {
    const isRequiredPassword = control.errors?.required;

    if (isRequiredPassword) {
      errorMessages.push('Password is required');
    }

    return errorMessages;
  }

  private getCompanyList(searchValue: string | number = ''): Observable<CompanyInterface.ICompanyListResponse> {
    return this.http.post<CompanyInterface.ICompanyListResponse>(`${this.apiUrl}/company/get-all`, { search: searchValue })
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  private downloadAdsTxt(data: any): Observable<any> {
    return this.http.get(`${this.apiUrl}/adstxt`, { params: data, responseType: 'blob' })
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  private updateCompany(company: CompanyInterface.ICompany): Observable<CompanyInterface.ICompanyUpdateResponse> {
    return this.http.put<CompanyInterface.ICompanyUpdateResponse>(`${this.apiUrl}/company/${company.id}`,
      JSON.stringify(company),
      { headers: new HttpHeaders().set('Content-Type', 'application/json') },
    )
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  private deleteCompanyById(id: number): Observable<CompanyInterface.ICompanyListResponse> {
    return this.http.delete<CompanyInterface.ICompanyListResponse>(`${this.apiUrl}/company/${id}`)
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  private createNewCompany(data: any): Observable<CompanyInterface.ICompanyCreateResponse> {
    return this.http.post<CompanyInterface.ICompanyCreateResponse>(
      `${this.apiUrl}/company`,
      JSON.stringify(data),
      { headers: new HttpHeaders().set('Content-Type', 'application/json') },
    )
      .pipe(catchError(this.errorsHandlingService.processError));
  }
}
